import React, { FC } from 'react'
import { Helmet } from 'react-helmet'

//* utils
import * as env from '../../../constants/environment.constants'
import { LocaleCodes } from '../../../constants/localization.constants'
import { ComponentSEO } from '../../../models/components/seo'
import { correctSlugPrefix } from '../../../utils/link.utils'
import { getDynamicTag } from './util/dynamicTags'

type Props = {
  path?: string
  seoMetadata?: ComponentSEO
  slug?: string
  locale?: string
  isPaginated?: boolean
  pageNum?: number
  isLastPage?: boolean
  hrefForPage?: any
  siteSection?:
    | 'Product'
    | 'Partners'
    | 'Homepage'
    | 'Flex'
    | 'Resource Demo'
    | 'PDF'
    | 'Solutions'
    | 'Customer Story'
    | 'Product Download'
    | 'Resource'
    | 'Legal Page'
    | 'Form Flex'
  availableLocales?: any
}

const SEOTags: FC<Props> = ({
  path,
  seoMetadata,
  slug,
  locale = LocaleCodes.ENGLISH.code,
  isPaginated = false,
  pageNum = 0,
  isLastPage = false,
  hrefForPage,
  siteSection = '',
  availableLocales,
}) => {
  const isChineseSite = locale === LocaleCodes.CHINESE.code
  const URL = isChineseSite ? env.URLS.CN_PROD : env.URLS.PROD

  let pageSlug = slug?.startsWith('cn') ? slug.substring(3) : slug || ''
  let pagePath = path?.startsWith('cn') ? path.substring(3) : path || '/'
  let canonicalURL = URL

  if (path) {
    let newPath = correctSlugPrefix(pagePath || '')
    canonicalURL = `${URL}${newPath}`
  } else if (pageSlug) {
    let newSlug = correctSlugPrefix(pageSlug)
    canonicalURL = `${URL}${newSlug}`
  }

  const seoData = {
    canonicalUrl: seoMetadata?.pageCanonicalUrl || '',
    metaDescription: seoMetadata?.pageMetaDescription || '',
    titleTag: seoMetadata?.pageTitleTag || '',
    ogtwitterImageUrl: seoMetadata?.ogtwitterImage?.file?.url || '',
    schemaOrgJsonLdData: seoMetadata?.pageSchemaOrgJsonLdData?.internal?.content || '',
    metaRobotsTag: seoMetadata?.pageMetaRobotsTag || '',
    vwoScriptEnabled: seoMetadata?.vwoScriptEnabled || false,
  }

  const dynamicSEOTags = getDynamicTag(
    seoData,
    canonicalURL,
    isPaginated,
    pageNum,
    isLastPage,
    hrefForPage,
    siteSection,
    pageSlug || '',
    availableLocales,
    URL,
    locale
  )

  const slugWithoutSlash = pageSlug[0] === '/' ? pageSlug.substring(1) : pageSlug
  const ogUrl = pageSlug === '' ? URL : `${URL}/${slugWithoutSlash}`

  return (
    <>
      <Helmet>
        {seoData.canonicalUrl && (
          <link rel="canonical" key={seoData.canonicalUrl} href={seoData.canonicalUrl} />
        )}

        {dynamicSEOTags}

        <meta property="og:url" content={ogUrl} />
        <meta property="og:type" content="page"></meta>
        <meta property="og:image:type" content="image/jpg" />
        <meta name="twitter:site" content="@uipath"></meta>
        <meta name="twitter:domain" content="www.uipath.com"></meta>
        <meta property="fb:app_id" content="2034514640109878" />
        <meta charSet="utf-8" />
        <meta http-equiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta name="author" content="UiPath Inc." />

        <link
          rel="shortcut icon"
          href="https://images.ctfassets.net/5965pury2lcm/4425FsZbwrsJdhttHkPlTE/61068f00208417f2017bce78bf2114b4/favicon-32x32.png"
        />

        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1"
        />

        <meta
          name="google-site-verification"
          content="uftQEsVNL_29Jh2NycRtYUtHEobYF30uDVYkqg_2Ju4"
        />

        <meta content="summary_large_image" name="twitter:card" />
        <meta name="theme-color" content="#131d40" />
        <meta name="alice" content="ed650dee-c9d2-4e5e-85af-4c4df1916b8c" />
      </Helmet>
    </>
  )
}

export default SEOTags
